import { Link } from '@tanstack/react-router';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '@workspace/ui/components/sidebar';
import {
  ArrowDownRight,
  BotMessageSquare,
  ChevronRight,
  ChevronsUpDown,
  Code,
  Coins,
  CreditCard,
  KeyRound,
  Landmark,
  LayoutDashboard,
  LinkIcon,
  Lock,
  Terminal,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '~/auth/use-auth';
import { ENABLE_CHAT } from '~/config/environment';
import { useOrganization } from '~/context/organization/use-org';
import { m } from '~/paraglide/messages.js';
import { ConnectAgentDialog } from './connect-agent-dialog';
import { NavUser } from './nav-user';
import { OrganizationLogo } from './organization-logo';

interface MenuItem {
  title: string;
  url: string;
  icon: React.ComponentType;
  locked?: boolean;
  action?: 'connect_agent';
  items?: MenuItem[];
}

const getMenuItems = (
  isBusinessUser: boolean,
  isKycVerified: boolean,
): MenuItem[] => [
  {
    title: m['sidebar.menu.dashboard'](),
    url: '/',
    icon: LayoutDashboard,
  },
  ...(ENABLE_CHAT
    ? [
        {
          title: m['sidebar.menu.assistant'](),
          url: '/chat',
          icon: BotMessageSquare,
        },
      ]
    : []),
  {
    title: m['sidebar.menu.connect_agent'](),
    url: '#',
    icon: Terminal,
    action: 'connect_agent' as const,
  },
  ...(isBusinessUser
    ? [
        {
          title: m['sidebar.menu.persons'](),
          url: '/persons',
          icon: Users,
        },
      ]
    : []),
  {
    title: m['sidebar.menu.receive'](),
    url: '#',
    icon: ArrowDownRight,
    locked: !isKycVerified,
    items: [
      {
        title: m['sidebar.menu.accounts'](),
        url: '/accounts',
        icon: Landmark,
        locked: !isKycVerified,
      },
      {
        title: m['sidebar.menu.payment_links'](),
        url: '/payment-links',
        icon: LinkIcon,
        locked: !isKycVerified,
      },
      {
        title: m['sidebar.menu.top_up'](),
        url: '/top-ups',
        icon: Coins,
        locked: !isKycVerified,
      },
    ],
  },
  {
    title: m['sidebar.menu.cards'](),
    url: '/cards',
    icon: CreditCard,
    locked: !isKycVerified,
  },
  {
    title: m['developer.menu.title'](),
    url: '#',
    icon: Code,
    items: [
      {
        title: m['developer.api_keys.title'](),
        url: '/dev',
        icon: KeyRound,
      },
      {
        title: m['developer.enterprise_api_keys.title'](),
        url: '/dev/business',
        icon: KeyRound,
      },
    ],
  },
];

export function AppSidebar() {
  const { isMobile, toggleSidebar } = useSidebar();
  const { user } = useAuth();
  const { organizations, currentOrganization, setCurrentOrganization } =
    useOrganization();
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);

  const isBusinessUser = user?.metadata.usage === 'business';
  const isKycVerified = user?.metadata?.kyc_verified || false;
  const isAgentConnected = user?.metadata?.agent_connected || false;
  const menuItems = getMenuItems(isBusinessUser, isKycVerified);

  return (
    <Sidebar collapsible={isMobile ? undefined : 'icon'}>
      <SidebarRail />

      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            {isBusinessUser && organizations.length > 0 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  >
                    <div className="text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                      <OrganizationLogo
                        logoUrl={currentOrganization?.profile?.logo_url}
                        alt="logo"
                        className="size-6 rounded"
                        fallbackClassName="size-6 rounded"
                      />
                    </div>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">
                        {currentOrganization?.profile?.legal_name ||
                          m['sidebar.organization_fallback']()}
                      </span>
                    </div>
                    {organizations.length > 1 && (
                      <ChevronsUpDown className="ml-auto" />
                    )}
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                {organizations.length > 1 && (
                  <DropdownMenuContent
                    className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                    align="start"
                    side={isMobile ? 'bottom' : 'right'}
                    sideOffset={4}
                  >
                    <DropdownMenuLabel className="text-muted-foreground text-xs">
                      {m['sidebar.organizations']()}
                    </DropdownMenuLabel>
                    {organizations.map((org) => (
                      <DropdownMenuItem
                        key={org.urn}
                        onClick={() => setCurrentOrganization(org)}
                        className="gap-2 p-2"
                      >
                        <div className="flex size-6 items-center justify-center rounded-md border">
                          <OrganizationLogo
                            logoUrl={org.profile?.logo_url}
                            alt="logo"
                            className="size-3.5 shrink-0 rounded"
                            fallbackClassName="size-3.5 shrink-0 rounded"
                          />
                        </div>
                        {org.profile?.legal_name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                )}
              </DropdownMenu>
            ) : (
              <SidebarMenuButton size="lg" className="cursor-default">
                <div className="text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                  <img src="/logo.png" alt="logo" className="size-6 rounded" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {user?.profile.full_name || m['sidebar.user_fallback']()}
                  </span>
                </div>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Application</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => {
                const hasChildren = !!item.items?.length;

                const handleClick = () => {
                  if (isMobile) toggleSidebar();
                };

                if (item.action === 'connect_agent') {
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        tooltip={item.title}
                        onClick={() => {
                          setConnectDialogOpen(true);
                          if (isMobile) toggleSidebar();
                        }}
                      >
                        {item.icon && <item.icon />}
                        <span>{item.title}</span>
                        {isAgentConnected && (
                          <span className="ml-auto h-2 w-2 rounded-full bg-green-500" />
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                }

                if (hasChildren) {
                  return (
                    <Collapsible
                      key={item.title}
                      defaultOpen
                      className="group/collapsible"
                    >
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton tooltip={item.title}>
                            {item.icon && <item.icon />}
                            <span>{item.title}</span>
                            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>

                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {item.items?.map((subItem) => (
                              <SidebarMenuSubItem key={subItem.title}>
                                <SidebarMenuSubButton asChild>
                                  <Link to={subItem.url} onClick={handleClick}>
                                    {subItem.icon && <subItem.icon />}
                                    <span>{subItem.title}</span>
                                    {subItem.locked && (
                                      <Lock className="ml-auto h-3 w-3 text-muted-foreground/50" />
                                    )}
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  );
                }

                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <Link to={item.url} onClick={handleClick}>
                        {item.icon && <item.icon />}
                        <span>{item.title}</span>
                        {item.locked && (
                          <Lock className="ml-auto h-3 w-3 text-muted-foreground/50" />
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
      </SidebarFooter>

      <ConnectAgentDialog
        open={connectDialogOpen}
        onOpenChange={setConnectDialogOpen}
      />
    </Sidebar>
  );
}
